import { useEffect, useState } from 'react';
import { IManagingWebSitesInstance } from '../../api/models';
import { WebSiteAction } from '../../constants/WebSiteAction';
import { EventType } from '../../constants/EventType';
import {
    ServerApplicationClientEventSummaryHandler,
    ServerApplicationClientEventSummaryHandlerArgs,
} from '../ApplicationServerManagement/ServerApplicationClientEventSummaryHandler';
import useRefedProp from '../../hooks/useRefedProp';
import { Permission } from '../../permissions/permission';
import { UserPrincipal } from '../../permissions/userPrincipal';
import { ServerApplicationClientEventSummary } from '../../signalr/models';
import signalREventsClient from '../../signalr/SignalREventsClient';
import { ServerWebSiteDetails } from '../WebSitesInfo/WebSitesInfoView';
import { InfoModal } from '../Shared/InfoModal';
import { ActionButton } from '../UIExtentions/Buttons';
import { WebSiteserverRowProps } from './WebSitesServerRow';

export interface WebSitesServerRowActionsProps {
    serverApp: IManagingWebSitesInstance;
    userPrincipal: UserPrincipal;
    serverUserPrincipal: UserPrincipal;
    onClickCallback: (action: WebSiteAction) => Promise<void>;
    onTimeoutCallback: (data: ServerApplicationClientEventSummary) => void;
}

export function WebSitesServerRowActions(props: WebSiteserverRowProps) {
    const [showInfo, setShowInfo] = useState(false);
    const [refreshSpinning, setRefreshSpinning] = useState(false);
    const onTimeoutCallbackRef = useRefedProp(props.onTimeoutCallback);

    const serverApp = props.serverApp;
    const userPrincipal = props.userPrincipal;
    const serverUserPrincipal = props.serverUserPrincipal;
    const onClickCallback = props.onClickCallback;

    const getEnableState = function (permission: Permission): boolean {
        return userPrincipal.hasPermission(permission) || serverUserPrincipal?.hasPermission(permission);
    };

    useEffect(() => {
        const webSiteRefreshHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.WebSiteRefreshTriggered],
            failedTypes: [EventType.WebSiteRefreshFailed],
            successTypes: [EventType.WebSiteRefreshSucceeded],
            emptyCallback: () => setRefreshSpinning(false),
            addCallback: () => setRefreshSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        function isForMe(data: ServerApplicationClientEventSummary) {
            return data.targetApplication === serverApp.name && data.targetServer === serverApp.serverName;
        }

        signalREventsClient.onWebSiteEvent.setHook(`WebSitesServerRow_${serverApp.name}_${serverApp.serverName}`, (data) => {
            if (isForMe(data)) {
                webSiteRefreshHandler.handleItem(data);
            }
        });
    }, [onTimeoutCallbackRef, serverApp.name, serverApp.serverName]);

    const infoModalTitle = `${serverApp.name} - ${serverApp.serverName}`;

    return (
        <>
            <td>
                <ActionButton
                    disabled={!getEnableState(Permission.WebSite_Refresh)}
                    spinning={refreshSpinning}
                    icon="fa-refresh"
                    variant="btn-dark-blue"
                    title="Refresh"
                    onClick={() => onClickCallback(WebSiteAction.Refresh)}
                />
            </td>
            <td></td>
            <td className="text-center">
                <ActionButton icon="fa-info-circle" variant="btn-dark-blue" title="Info" onClick={() => setShowInfo(true)} />
                <InfoModal title={infoModalTitle} show={showInfo} onHide={() => setShowInfo(false)}>
                    <ServerWebSiteDetails serverApp={serverApp} />
                </InfoModal>
            </td>
        </>
    );
}
